"use client";

import React, { useState, useRef, useContext, useEffect } from "react";
import { ProductContext } from "../context/ProductContext";
import toast from "react-hot-toast";

import CustomizationModal from "./CustomizationModal";
import StarRating from "./StarRating";
import {
  ChevronDown,
  ChevronUp,
  Minus,
  Plus,
  Copy,
  GripVertical,
  Heart,
  Share2,
  Play,
  Palette,
  Ruler,
  Package,
  AlertCircle,
  Home,
  ArrowLeft,
  Shield,
  ShoppingCart,
  Clipboard,
  Link,
  Lock,
  HeartCrack,
  AlertTriangle,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import { usePromoCodes } from "../context/PromoCodeContext";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";

const ProductDetails = () => {
  const {
    product,
    loading,
    averageRating,
    totalReviews,
    reviewsLoading,
    productError,
  } = useContext(ProductContext);
  const {
    promoCodes,
    loading: promoLoading,
    error: promoError,
    fetchPromoCodes,
  } = usePromoCodes();

  const user = JSON.parse(localStorage.getItem("user")); // or use your AuthContext
  const navigate = useNavigate();

  const { addToCart } = useCart();
  const { toggleWishlistItem, isInWishlist } = useWishlist();

  const isFavorited = user ? isInWishlist(user.id, product?.id) : false;

  const offersScrollRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    if (product?.id) {
      fetchPromoCodes(product.id);
    }
  }, [product?.id]);

  useEffect(() => {
    console.log(product?.colorCode);
  }, [product?.colorCode]);

  /* ---------------- STATE ---------------- */
  const [activeMedia, setActiveMedia] = useState("");
  const [activeMediaType, setActiveMediaType] = useState("image"); // "image" or "video"
  const [quantity, setQuantity] = useState(1); // Will be updated based on minimumOrderQty
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [isCustomizationModalOpen, setIsCustomizationModalOpen] =
    useState(false);
  const [isShared, setIsShared] = useState(false);
  const [imageAspectRatio, setImageAspectRatio] = useState(null);
  const [isImageTall, setIsImageTall] = useState(false);

  /* ---------------- EFFECTS ---------------- */
  useEffect(() => {
    if (product?.image) {
      setActiveMedia(product.image);
      setActiveMediaType("image");
    }
  }, [product?.id]);

  useEffect(() => {
    if (product?.id) {
      localStorage.setItem("lastProductId", product.id);
    }
  }, [product?.id]);

  // Set initial quantity based on minimum order quantity
  useEffect(() => {
    if (product) {
      const minimumOrderQty = product?.minimumOrderQty || product?.min_order_qty || 1;
      setQuantity(minimumOrderQty);
    }
  }, [product]);

  // Handle image aspect ratio detection
  useEffect(() => {
    if (activeMedia && activeMediaType === "image") {
      const img = new Image();
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        setImageAspectRatio(aspectRatio);
        // Consider image "tall" if height is significantly greater than width
        setIsImageTall(aspectRatio < 0.75); // height is more than 1.33x the width
      };
      img.src = activeMedia;
    } else {
      setIsImageTall(false);
      setImageAspectRatio(null);
    }
  }, [activeMedia, activeMediaType]);

  /* ---------------- LOADING ---------------- */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  /* ---------------- ERROR STATE ---------------- */
  if (productError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md mx-auto">
          <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            {productError === "No such product exists"
              ? "Product Not Found"
              : "Error Loading Product"}
          </h1>
          <p className="text-gray-600 mb-8 text-lg">
            {productError === "No such product exists"
              ? "The product you're looking for doesn't exist or may have been removed."
              : productError}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Go Back
            </button>
            <button
              onClick={() => navigate("/")}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
            >
              <Home className="w-5 h-5" />
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md mx-auto">
          <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
            <Package className="w-10 h-10 text-gray-400" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            Product Not Found
          </h1>
          <p className="text-gray-600 mb-8 text-lg">
            The product you're looking for doesn't exist or may have been
            removed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Go Back
            </button>
            <button
              onClick={() => navigate("/")}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
            >
              <Home className="w-5 h-5" />
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handlePersonalizeClick = () => {
    setIsCustomizationModalOpen(true);
  };

  /* ---------------- DERIVED DATA ---------------- */
  const images = product.images || [];
  const video = product.video || product.videoUrl;
  const price = product.price;
  const offerPrice = product.offerPrice || price;

  // Create media array combining images and video
  const mediaItems = [
    ...images.map((img) => ({ type: "image", url: img })),
    ...(video ? [{ type: "video", url: video }] : []),
  ];

  const discountPercent =
    price && offerPrice && price > offerPrice
      ? Math.round(((price - offerPrice) / price) * 100)
      : 0;
  const totalPrice = offerPrice * quantity;

  // Get minimum order quantity
  const minimumOrderQty = product?.minimumOrderQty || product?.min_order_qty || 1;

  /* ---------------- SIMPLIFIED RATING DISPLAY LOGIC ---------------- */
  // Determine which rating data to use with proper validation
  let displayRating = 0;
  let displayRatingCount = 0;
  let showRatings = true;

  console.log("Rating Debug Info:", {
    reviewsLoading,
    averageRating,
    totalReviews,
    productAverageRating: product?.averageRating,
    productRating: product?.rating,
    productRatingCount: product?.ratingCount
  });

  if (reviewsLoading) {
    // Show loading state for ratings
    displayRating = 0;
    displayRatingCount = 0;
    showRatings = false;
  } else {
    // Priority 1: Use context rating data (from reviews API)
    const contextRating = Number(averageRating);
    const contextCount = Number(totalReviews);

    // Priority 2: Use product's average_rating from API
    const productApiRating = Number(product?.averageRating);

    // Priority 3: Use legacy product rating data
    const legacyRating = Number(product?.rating || 0);
    const legacyCount = Number(product?.ratingCount || 0);

    if (!isNaN(contextRating) && contextCount > 0) {
      // Use context data when available and valid
      displayRating = Math.max(0, Math.min(5, contextRating));
      displayRatingCount = contextCount;
    } else if (!isNaN(productApiRating)) {
      // Use product API rating (even if 0.0)
      displayRating = Math.max(0, Math.min(5, productApiRating));
      displayRatingCount = 0; // API doesn't provide count
    } else if (!isNaN(legacyRating) && (legacyRating > 0 || legacyCount > 0)) {
      // Use legacy data only if there's actual rating data
      displayRating = Math.max(0, Math.min(5, legacyRating));
      displayRatingCount = legacyCount;
    } else {
      // No rating data available
      displayRating = 0;
      displayRatingCount = 0;
    }
  }

  console.log("Final Rating Display:", {
    displayRating,
    displayRatingCount,
    showRatings
  });

  /* ---------------- OFFERS (UNCHANGED) ---------------- */
  const getOfferColors = (id) => {
    const colors = [
      {
        bg: "bg-amber-500",
        text: "text-amber-600",
        border: "border-amber-500",
      },
      {
        bg: "bg-indigo-600",
        text: "text-indigo-600",
        border: "border-indigo-600",
      },
      {
        bg: "bg-green-600",
        text: "text-green-600",
        border: "border-green-600",
      },
      {
        bg: "bg-purple-600",
        text: "text-purple-600",
        border: "border-purple-600",
      },
    ];

    return colors[id % colors.length];
  };

  const incrementQuantity = () => {
    setQuantity((q) => q + 1); // No maximum limit
  };

  const decrementQuantity = () => {
    setQuantity((q) => Math.max(minimumOrderQty, q - 1)); // Can't go below minimum order quantity
  };

  const copyToClipboard = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Code "${code}" copied to clipboard!`, {
        icon: <Clipboard size={16} className="text-blue-600" />,
      });
    } catch (error) {
      toast.error("Failed to copy code");
    }
  };

  const handleMouseDown = (e) => {
    const slider = offersScrollRef.current;
    const startX = e.pageX - slider.offsetLeft;
    const scrollLeft = slider.scrollLeft;

    const onMove = (e) => {
      const x = e.pageX - slider.offsetLeft;
      slider.scrollLeft = scrollLeft - (x - startX) * 2;
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener(
      "mouseup",
      () => document.removeEventListener("mousemove", onMove),
      { once: true },
    );
  };

  const handleShare = async () => {
    setIsShared(true);

    try {
      if (navigator.share) {
        await navigator.share({
          title: product.name,
          text: `Check out this amazing product: ${product.name}`,
          url: window.location.href,
        });
        toast.success("Product shared successfully!", {
          icon: <Link size={16} className="text-blue-600" />,
        });
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Product link copied to clipboard!", {
          icon: <Link size={16} className="text-blue-600" />,
        });
      }
    } catch (error) {
      // Handle user cancellation or other errors
      if (error.name !== "AbortError") {
        toast.error("Failed to share product");
      }
    }

    setTimeout(() => setIsShared(false), 800);
  };

  const handleWishlistToggle = async () => {
    if (!user) {
      toast.error("Please sign in to add items to your wishlist", {
        icon: <Lock size={16} className="text-red-600" />,
      });
      navigate("/signin");
      return;
    }

    try {
      const wasInWishlist = isFavorited;
      await toggleWishlistItem(user.id, product);

      if (wasInWishlist) {
        toast.success(`${product.name} removed from wishlist`, {
          icon: <HeartCrack size={16} className="text-red-600" />,
        });
      } else {
        toast.success(`${product.name} added to wishlist!`, {
          icon: <Heart size={16} className="text-red-600" fill="currentColor" />,
        });
      }
    } catch (error) {
      toast.error("Failed to update wishlist");
    }
  };

  const handleMediaClick = (mediaItem) => {
    setActiveMedia(mediaItem.url);
    setActiveMediaType(mediaItem.type);
  };

  // Helper function to validate color code format
  const isValidColorCode = (colorCode) => {
    if (!colorCode) return false;
    // Check if it's a valid hex color (with or without #)
    const hexPattern = /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexPattern.test(colorCode);
  };

  // Helper function to format color code
  const formatColorCode = (colorCode) => {
    if (!colorCode) return null;
    // Ensure color code starts with #
    return colorCode.startsWith("#") ? colorCode : `#${colorCode}`;
  };

  // Count available specifications to determine grid layout
  const availableSpecs = [
    product.color,
    product.dimensions,
    product.material,
    product.product_policy,
  ].filter(Boolean);

  const hasProductPolicy = Boolean(product.product_policy);
  const gridCols = hasProductPolicy
    ? "grid-cols-1 sm:grid-cols-2"
    : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";

  /* ---------------- RENDER ---------------- */
  return (
    <>
      <section className="px-4 lg:px-8 xl:px-12 mt-8 sm:mt-12 md:mt-16 lg:mt-4">
        <div className="max-w-7xl mx-auto">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-12 ${isImageTall ? 'items-start' : 'items-stretch'}`}>
            {/* Left Side - Media Gallery - Dynamic height based on image aspect ratio */}
            <div className="w-full flex flex-col">
              <div className="flex gap-3 flex-1">
                {/* Media Thumbnail Grid - Vertical - Made Smaller */}
                {mediaItems.length > 0 && (
                  <div className="flex flex-col gap-1.5 sm:gap-2 w-14 sm:w-16 md:w-18 flex-shrink-0 h-full overflow-y-auto">
                    {mediaItems.map((mediaItem, index) => (
                      <button
                        key={`${mediaItem.type}-${index}`}
                        onClick={() => handleMediaClick(mediaItem)}
                        className={`border-2 rounded-md overflow-hidden aspect-square relative transition-all duration-200 flex-shrink-0 ${
                          activeMedia === mediaItem.url
                            ? "border-teal-500 shadow-md"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        {mediaItem.type === "video" ? (
                          <>
                            <video
                              src={mediaItem.url}
                              className="w-full h-full object-contain"
                              muted
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                              <Play
                                className="w-3 h-3 text-white"
                                fill="white"
                              />
                            </div>
                          </>
                        ) : (
                          <img
                            src={mediaItem.url}
                            alt="thumbnail"
                            className="w-full h-full object-contain"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Main Media Container - Dynamic height handling */}
                <div className="flex-1">
                  <div
                    className={`w-full rounded-xl overflow-hidden bg-gray-50 flex items-center justify-center shadow-sm border border-gray-100 ${
                      isImageTall
                        ? 'h-[400px] sm:h-[500px] md:h-[600px] lg:h-[700px]'
                        : 'h-full min-h-[400px] sm:min-h-[500px] md:min-h-[600px]'
                    }`}
                  >
                    {activeMediaType === "video" ? (
                      <video
                        src={activeMedia}
                        controls
                        className="w-full h-full object-contain"
                        poster={product.image}
                      >
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <img
                        ref={imageRef}
                        src={activeMedia}
                        alt={product.name}
                        className={`w-full h-full ${
                          isImageTall ? 'object-contain' : 'object-contain'
                        }`}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Product Info */}
            <div className="space-y-4 sm:space-y-5 md:space-y-6 mt-4 sm:mt-0 flex flex-col">
              <header className="flex items-start justify-between gap-4">
                <h1
                  className={`${product.name && product.name.length > 30 ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl"} font-semibold text-teal-800 leading-tight`}
                >
                  {product.name}
                </h1>
              </header>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <p className="text-xs sm:text-sm uppercase text-zinc-600 font-medium">
                    Category: {product.categoryId}
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={handleWishlistToggle}
                    className={`p-2 rounded-full transition-colors ${isFavorited ? "text-red-500 bg-red-50" : "text-gray-600 hover:bg-gray-100"}`}
                    aria-label="Add to wishlist"
                  >
                    <Heart
                      className={`w-5 h-5 sm:w-6 sm:h-6 ${isFavorited ? "fill-current" : ""}`}
                    />
                  </button>

                  <button
                    onClick={handleShare}
                    className="p-2 rounded-full text-gray-600 hover:bg-gray-100 transition-colors"
                    aria-label="Share product"
                  >
                    <Share2 className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                </div>
              </div>

              {/* Price + Rating */}
              <div className="space-y-2 sm:space-y-3">
                {/* Price Section */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <span className="text-sm sm:text-base font-medium">MRP:</span>
                  {offerPrice ? (
                    <>
                      <span className="text-lg sm:text-xl text-zinc-800 line-through">
                        ₹{price}
                      </span>

                      <span className="text-xl sm:text-2xl font-bold text-red-500">
                        ₹{Math.round(offerPrice)}
                      </span>

                      {discountPercent > 0 && (
                        <span className="bg-green-600 text-white text-xs sm:text-sm px-2 py-1 rounded-md font-medium">
                          {discountPercent}% OFF
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-xl sm:text-2xl font-bold text-red-500">
                      ₹{price}
                    </span>
                  )}
                </div>

                {/* Rating and Tax Info */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                  <span className="text-sm sm:text-base text-gray-500">
                    (Inclusive of all taxes)
                  </span>

                  <div className="flex items-center gap-3">
                    <StarRating
                      rating={displayRating}
                      inactive={displayRating === 0}
                      size={18}
                    />

                    {reviewsLoading ? (
                      <span className="text-sm text-gray-500">
                        Loading ratings...
                      </span>
                    ) : showRatings ? (
                      <span className="text-sm sm:text-base font-semibold text-gray-700">
                        {displayRating === 0 ? "0.0" : displayRating.toFixed(1)}{" "}
                        {displayRatingCount > 0
                          ? `(${displayRatingCount} ${displayRatingCount === 1 ? "review" : "reviews"})`
                          : ""}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">
                        No ratings yet
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Enhanced Product Specifications */}
              <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-xl py-2 sm:py-3 px-4 sm:px-6 border border-gray-200 shadow-sm">
                <h3 className="font-bold text-base sm:text-lg text-gray-800 mb-2 flex items-center gap-2">
                  <Package className="w-5 h-5 text-teal-600" />
                  Product Specifications
                </h3>
                <div className={`grid ${gridCols} gap-4`}>
                  {product.color && (
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Palette className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600 block">
                            Color
                          </span>
                          <span className="text-sm text-gray-800 font-semibold">
                            {product.color}
                          </span>
                        </div>
                      </div>
                      {/* Use dynamic color code from API */}
                      {product.colorCode && (
                        <div
                          className="w-8 h-8 rounded-full border-1 border-gray-400 shadow-sm"
                          style={{
                            backgroundColor: `${product.colorCode}`,
                          }}
                          title={`${product.color} - ${product.colorCode}`}
                        />
                      )}
                    </div>
                  )}

                  {product.dimensions && (
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Ruler className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600 block">
                            Dimensions
                          </span>
                          <span className="text-sm text-gray-800 font-semibold">
                            {product.dimensions}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {product.material && (
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Package className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600 block">
                            Material
                          </span>
                          <span className="text-sm text-gray-800 font-semibold">
                            {product.material}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {product.product_policy && (
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-orange-100 rounded-lg">
                          <Shield className="w-4 h-4 text-orange-600" />
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-600 block">
                            Return Policy
                          </span>
                          <span className="text-sm text-gray-800 font-semibold">
                            {product.product_policy}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Available Offers - Horizontal Scroll */}
              {promoCodes.length > 0 && (
                <div className="bg-white rounded-xl px-4 sm:px-5 md:px-6 shadow-lg border border-gray-100">
                  <div
                    ref={offersScrollRef}
                    className="flex gap-3 md:gap-4 overflow-x-auto scrollbar-hide cursor-grab active:cursor-grabbing py-2 select-none"
                    onMouseDown={handleMouseDown}
                    onSelectStart={(e) => e.preventDefault()}
                    style={{
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                      userSelect: "none",
                      WebkitUserSelect: "none",
                      MozUserSelect: "none",
                    }}
                  >
                    {promoCodes.map((promo) => {
                      const colors = getOfferColors(promo.id);

                      return (
                        <div
                          key={promo.id}
                          className={`flex-shrink-0 w-64 md:w-80 p-3 md:p-4 bg-gray-50 rounded-lg md:rounded-xl border-2 border-dashed ${colors.border} shadow-sm hover:shadow-md transition-shadow select-none`}
                          onSelectStart={(e) => e.preventDefault()}
                        >
                          <h3
                            className={`text-sm md:text-lg font-semibold mb-2 md:mb-3 ${colors.text}`}
                          >
                            {promo.title}
                          </h3>

                          <p className="text-xs text-gray-600 mb-2">
                            {promo.description}
                          </p>

                          <div
                            className={`inline-flex items-center gap-2 px-2 md:px-3 py-1 md:py-2 rounded-md md:rounded-lg border-2 border-dashed bg-opacity-10 ${colors.bg} ${colors.border} ${colors.text}`}
                          >
                            <span>Code: {promo.code || "No code needed"}</span>

                            {promo.code && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(promo.code);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                className="p-1 hover:bg-white hover:bg-opacity-50 rounded transition-colors select-auto"
                                style={{ userSelect: "auto" }}
                              >
                                <Copy className="w-3 h-3 md:w-4 md:h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="space-y-4 flex-1 flex flex-col justify-end">
                {/* Quantity Selector */}
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <div className="flex items-center gap-3">
                    <label className="text-sm sm:text-base font-medium text-gray-700 whitespace-nowrap">
                      Quantity:
                    </label>
                    <div className="flex items-center border-2 bg-white border-black rounded-lg">
                      <button
                        onClick={decrementQuantity}
                        className="p-2 sm:p-3 hover:bg-gray-100 transition-colors"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                      <span className="px-4 sm:px-6 py-2 sm:py-3 text-base sm:text-lg font-medium min-w-[50px] sm:min-w-[60px] text-center">
                        {quantity}
                      </span>
                      <button
                        onClick={incrementQuantity}
                        className="p-2 sm:p-3 hover:bg-gray-100 transition-colors"
                        aria-label="Increase quantity"
                      >
                        <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Minimum order quantity indicator */}
                  {quantity < minimumOrderQty && (
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-orange-600 font-medium">
                        Minimum order: {minimumOrderQty} units
                      </span>
                    </div>
                  )}
                </div>

                {/* Add to Cart Button */}
                <button
                  onClick={() => {
                    if (!user) {
                      toast.error("Please sign in to add items to cart", {
                        icon: <Lock size={16} className="text-red-600" />,
                      });
                      navigate("/signin");
                      return;
                    }

                    if (quantity < minimumOrderQty) {
                      toast.error(`Minimum order quantity is ${minimumOrderQty} units`, {
                        icon: <AlertTriangle size={16} className="text-orange-600" />,
                      });
                      return;
                    }

                    try {
                      addToCart(
                        {
                          ...product,
                          price: Number(product.offerPrice),
                          offerPrice: Number(
                            product.offerPrice || product.price,
                          ),
                        },
                        quantity,
                        { personalizedText: "Custom Text", color: "red" },
                      );

                      toast.success(`${product.name} added to cart!`, {
                        icon: <ShoppingCart size={16} className="text-green-600" />,
                      });

                      navigate("/cart");
                    } catch (error) {
                      toast.error("Failed to add item to cart");
                    }
                  }}
                  disabled={quantity < minimumOrderQty}
                  className={`w-full py-3 sm:py-4 rounded-lg md:rounded-xl font-bold text-sm sm:text-base md:text-lg transition-colors focus:outline-none focus:ring-2 ${
                    quantity < minimumOrderQty
                      ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                      : "bg-zinc-800 text-white hover:bg-zinc-900 focus:ring-zinc-600"
                  }`}
                >
                  {quantity < minimumOrderQty
                    ? `Minimum Order: ${minimumOrderQty} units`
                    : "Add to Cart"
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CustomizationModal
        isOpen={isCustomizationModalOpen}
        onClose={() => setIsCustomizationModalOpen(false)}
        product={product}
      />
    </>
  );
};

export default ProductDetails;
